import './TeamSection.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { formatLongDate } from '../../../../shared/helpers/date/formatLongDate';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import ColorTag, { ColorTagColor } from '../../../../shared/components/color-tag/ColorTag';
import RoleTag from '../../../../shared/components/color-tag/RoleTag';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';
import useToast from '../../../../shared/hooks/global/UseToast';
import { TEAM_ADD, WIZARD_RETURN_AGENCY_HUB, WIZARD_RETURN_PARAM } from '../../../../shared/routes/path';
import { RoleType } from '../../../../shared/types/user/accounts/RoleType';
import type { TeamResponse } from '../../../../shared/models/team/TeamResponse';
import type { TeamAssignableRole } from '@brand24-original/llm-types';
import {
  changeMemberRole,
  removeUser,
  revokeGuestProject,
  updateGuestProjects,
} from '../../../../shared/services/team/TeamService';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import {
  AgencyHubMember,
  MemberStatus,
  memberDisplayName,
  toAgencyHubMember,
} from '../../agencyHubRows';
import { AGENCY_HUB_TEAM_QUERY } from '../../agencyHubQueryKeys';
import GuestScopeDialog from './GuestScopeDialog';

type TeamGroup = 'ADMINS' | 'USERS' | 'GUESTS';

const GROUP_ORDER: TeamGroup[] = ['ADMINS', 'USERS', 'GUESTS'];
// The account owner sits in Admins (their "Owner" tag tells them apart) — no group for one person.
const GROUP_OF: Record<RoleType, TeamGroup> = {
  OWNER: 'ADMINS',
  ADMIN: 'ADMINS',
  USER: 'USERS',
  GUEST: 'GUESTS',
};
const GROUP_COPY: Record<TeamGroup, { nameKey: string; noteKey: string }> = {
  ADMINS: { nameKey: 'admins', noteKey: 'agencyHubAdminsNote' },
  USERS: { nameKey: 'users', noteKey: 'agencyHubUsersNote' },
  GUESTS: { nameKey: 'guests', noteKey: 'agencyHubGuestsNote' },
};
const STATUS_COLOR: Record<MemberStatus, ColorTagColor> = { ACTIVE: 'green', PENDING: 'orange' };
const STATUS_KEY: Record<MemberStatus, string> = { ACTIVE: 'active', PENDING: 'pending' };

type SortField = 'name' | 'invited';

/** Table row: the member plus the group it renders under and its sortable display name. */
type MemberRow = AgencyHubMember & { group: TeamGroup; name: string };

interface TeamSectionProps {
  /** `GET /team/data`. */
  team: TeamResponse[];
  /** The account's projects — names for the guests' chips and the "All projects · N" count. */
  projects: { id: number; name: string }[];
  /** The signed-in member: no actions on their own row (as on the Team page). */
  currentUserId: number;
  maxUsers: number;
}

const invitePath = `${TEAM_ADD}?${WIZARD_RETURN_PARAM}=${WIZARD_RETURN_AGENCY_HUB}`;

/**
 * The hub's team list: the projects section's frame (title + tooltip, "Invite member" where "Add new
 * project" sits) over ONE table whose rows are grouped by role with PrimeReact's `subheader` row groups
 * — Admins (owner + admins), Users, Guests. No search, filters, selection or paging: a team is a dozen
 * people and paging would cut groups.
 *
 * Reads are live (`/team/data`). Live writes: the role dropdown (`PATCH /team/data/:id/role`, a
 * guest-to-be picks the projects in the scope dialog first), a guest's scope (dialog and the chips'
 * "x"), removing a member (`DELETE /team/data/:id`, after a confirmation with the name). Resending an
 * invitation is not offered until its endpoint exists (the invitation lifecycle stage). Inviting goes
 * to the real invite page with `?from=agency-hub`, which brings the user back here.
 */
const TeamSection: React.FC<TeamSectionProps> = ({ team, projects, currentUserId, maxUsers }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [members, setMembers] = useState<AgencyHubMember[]>(() => team.map(toAgencyHubMember));
  useEffect(() => setMembers(team.map(toAgencyHubMember)), [team]);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  // Scope dialog target: an existing guest (edit) or a member being switched TO guest — that role
  // change only lands with a non-empty scope, so the dropdown stays put until the dialog saves.
  const [scopeTarget, setScopeTarget] = useState<AgencyHubMember | null>(null);

  // `value` as the key on purpose: PrimeReact 10.7.0 misspells `optionValue` inside the Dropdown
  // (`props.OptionValue`), so any other key renders the control empty.
  const roleOptions = [
    { label: t('admin'), value: 'ADMIN' },
    { label: t('user'), value: 'USER' },
    { label: t('guest'), value: 'GUEST' },
  ];

  // Sorting stays INSIDE the groups: the table is `lazy`, so PrimeReact only reports the sort and the
  // rows arrive already ordered by group, then by the chosen column (name by default).
  const { rows, groupSizes } = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const direction = sortOrder === -1 ? -1 : 1;
    const byColumn = (a: MemberRow, b: MemberRow) =>
      sortField === 'invited'
        ? (a.invited.getTime() - b.invited.getTime()) * direction
        : collator.compare(a.name, b.name) * (sortField === 'name' ? direction : 1);
    const sorted: MemberRow[] = members
      .map((member) => ({
        ...member,
        group: GROUP_OF[member.role],
        name: `${member.firstName} ${member.lastName}`,
      }))
      .sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group) || byColumn(a, b));
    const sizes: Record<TeamGroup, number> = { ADMINS: 0, USERS: 0, GUESTS: 0 };
    sorted.forEach((row) => (sizes[row.group] += 1));
    return { rows: sorted, groupSizes: sizes };
  }, [members, sortField, sortOrder]);

  // An existing guest's scope is saved for real (`PUT /team/data/:id/projects`, the Team page's
  // endpoint); the refreshed team list then redraws the chips.
  const queryClient = useQueryClient();
  const [scopeError, setScopeError] = useState<string | null>(null);
  const { mutate: saveGuestScope, isPending: scopeSaving } = useMutation({
    mutationFn: ({ id, projectIds }: { id: number; projectIds: number[] }) =>
      updateGuestProjects(id, projectIds),
    onSuccess: (saved) => {
      if (!saved) {
        setScopeError(t('somethingWentWrong'));
        return;
      }
      void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_TEAM_QUERY] });
      setScopeTarget(null);
    },
  });
  const closeScope = () => {
    setScopeTarget(null);
    setScopeError(null);
  };

  // The "x" on a project chip takes that one project away (`DELETE /team/data/:id/projects/:projectId`
  // — one grant, so a parallel change to the guest's other projects is never overwritten). Success
  // and failure both refresh the list (the chip goes, or comes back); a failure also says so.
  const { showToast, toastElement } = useToast();
  const { mutate: revokeProject, isPending: revoking } = useMutation({
    mutationFn: ({ id, projectId }: { id: number; projectId: number }) =>
      revokeGuestProject(id, projectId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_TEAM_QUERY] }),
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_TEAM_QUERY] });
      showToast(t('somethingWentWrong'), 'error');
    },
  });
  // The role dropdown saves at once (no confirmation — decision 2026-10-23); a toast says what
  // happened, and the refreshed list is the dropdown's new value. Leaving the guest role drops the
  // member's project shares, which the toast spells out. A failure refreshes the list (the dropdown
  // springs back) and says so.
  const { mutate: saveRole, isPending: roleSaving } = useMutation({
    mutationFn: ({
      member,
      role,
      projectIds,
    }: {
      member: AgencyHubMember;
      role: TeamAssignableRole;
      projectIds?: number[];
    }) => changeMemberRole(member.id, role, projectIds),
    onSuccess: (saved, { member }) => {
      void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_TEAM_QUERY] });
      const options = { name: memberDisplayName(member), role: t(saved.role.toLowerCase()) };
      showToast(t('roleChanged', options));
      closeScope();
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_TEAM_QUERY] });
      showToast(t('somethingWentWrong'), 'error');
    },
  });

  const changeRole = (member: AgencyHubMember, role: RoleType) => {
    if (role === member.role || role === 'OWNER') return;
    // To guest: pick the projects first (the "Assign" dialog); its Save sends the role with them.
    if (role === 'GUEST') {
      setScopeTarget({ ...member, projectIds: [] });
      return;
    }
    saveRole({ member, role });
  };

  const saveScope = (projectIds: number[]) => {
    if (!scopeTarget) return;
    setScopeError(null);
    if (scopeTarget.role === 'GUEST') {
      saveGuestScope({ id: scopeTarget.id, projectIds });
      return;
    }
    saveRole({ member: scopeTarget, role: 'GUEST', projectIds });
  };

  // Removal on the Team page's endpoint (`DELETE /team/data/:id`); its service swallows errors and a
  // denial answers `false`, so success is exactly `true`. The freed seat lives in the account context
  // (it gates "Invite member"), hence the account refetch next to the list.
  const { refetchAccountSilently } = useAccount();
  const { mutate: removeMember, isPending: removingMember } = useMutation({
    mutationFn: async (id: number) => (await removeUser(id)) === true,
    onSuccess: (removed) => {
      if (!removed) {
        showToast(t('somethingWentWrong'), 'error');
        return;
      }
      void queryClient.invalidateQueries({ queryKey: [AGENCY_HUB_TEAM_QUERY] });
      void refetchAccountSilently();
      showToast(t('memberRemoved'));
    },
    onError: () => showToast(t('somethingWentWrong'), 'error'),
  });
  const confirmRemove = (member: AgencyHubMember) =>
    confirmDialog({
      message: t('removeMemberConfirm', { name: memberDisplayName(member) }),
      header: t('areYouSure'),
      // PrimeReact's own "Yes / No" fall back to its built-in `en` locale (the app sets none), so the
      // buttons are translated here.
      acceptLabel: t('yes'),
      rejectLabel: t('no'),
      defaultFocus: 'reject',
      accept: () => removeMember(member.id),
    });

  const groupHeader = (row: MemberRow) => {
    const copy = GROUP_COPY[row.group];
    return (
      <div className={`agency-hub-team__group agency-hub-team__group--${row.group.toLowerCase()}`}>
        <span className="agency-hub-team__group-name">{t(copy.nameKey)}</span>
        <span className="agency-hub-team__group-count">{groupSizes[row.group]}</span>
        <span className="agency-hub-team__group-note">{t(copy.noteKey)}</span>
      </div>
    );
  };

  const memberBody = (row: MemberRow) => (
    <div className="agency-hub-team__member">
      <span className="agency-hub-team__name">{row.name}</span>
      <span className="agency-hub-team__email">{row.email}</span>
    </div>
  );

  // The owner is not reassignable: a static tag instead of the dropdown.
  const roleBody = (row: MemberRow) =>
    row.role === 'OWNER' ? (
      <RoleTag role={row.role} />
    ) : (
      <Dropdown
        value={row.role}
        options={roleOptions}
        optionLabel="label"
        optionValue="value"
        onChange={(e) => changeRole(row, e.value as RoleType)}
        disabled={roleSaving}
        className="dropdown agency-hub-team__role"
        itemTemplate={(option) => <div className="p-dropdown-item">{option.label}</div>}
      />
    );

  const projectsBody = (row: MemberRow) => {
    if (row.role !== 'GUEST') {
      return t('allProjectsCount', { count: projects.length });
    }
    const shared = projects.filter((project) => row.projectIds.includes(project.id));
    return (
      <div className="agency-hub-team__scope">
        {/* Chips with an "x"; the last project has none — a guest needs at least one, the dialog and
            the backend enforce the same. */}
        {shared.map((project) => (
          <ColorTag key={project.id} color="grey" className="agency-hub-team__chip">
            {project.name}
            {shared.length > 1 && (
              <Button
                className="btn btn--link agency-hub-team__chip-remove"
                aria-label={t('remove')}
                disabled={revoking}
                onClick={() => revokeProject({ id: row.id, projectId: project.id })}
              >
                <span className="ico--close ico--xxs" />
              </Button>
            )}
          </ColorTag>
        ))}
        <Button
          className="btn btn--link agency-hub-team__assign"
          onClick={() => setScopeTarget(row)}
          label={t('assign')}
          icon={<span className="ico--plus ico--xxs" />}
        />
      </div>
    );
  };

  const statusBody = (row: MemberRow) => (
    <ColorTag color={STATUS_COLOR[row.status]}>{t(STATUS_KEY[row.status])}</ColorTag>
  );

  // Team page rule: a single action stands in the cell by itself (the "…" menu is for two or more,
  // and removal is the only one until resending an invitation has an endpoint). The owner and the
  // signed-in member have none.
  const actionsBody = (row: MemberRow) => {
    if (row.role === 'OWNER' || row.id === currentUserId) return null;
    return (
      <div className="table__cell--actions">
        <Button
          id={`agency-hub-member-remove-${row.id}`}
          className="action-btn"
          disabled={removingMember}
          onClick={() => confirmRemove(row)}
        >
          <span className="ico--trash ico--xs" />
        </Button>
        <CustomTooltip tooltipText="removeMember" targetRef={`agency-hub-member-remove-${row.id}`} />
      </div>
    );
  };

  const inviteDisabled = team.length >= maxUsers;

  return (
    <div className="stats__panel agency-hub-team">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('team')}
          <CustomTooltip tooltipText="agencyHubTeamTooltip" addMargin={false} />
          {/* Phone: inviting as a small button next to the title (the toolbar one is desktop-only). */}
          {isMobile && (
            <Button
              className="btn btn--sm"
              onClick={() => navigate(invitePath)}
              disabled={inviteDisabled}
              aria-label={t('inviteMember')}
            >
              <span className="ico--add ico--xs" />
            </Button>
          )}
        </div>
        {!isMobile && (
          <div className="page-box__buttons agency-hub-page__toolbar">
            <Button
              className="btn btn--sm"
              onClick={() => navigate(invitePath)}
              disabled={inviteDisabled}
              label={t('inviteMember')}
              icon={<span className="ico--add ico--xs" />}
            />
          </div>
        )}
      </div>

      <DataTable
        value={rows}
        dataKey="id"
        lazy
        scrollable
        removableSort
        sortField={sortField ?? undefined}
        sortOrder={sortOrder}
        onSort={(e) => {
          setSortField((e.sortField as SortField) || null);
          // `removableSort`: the third click reports no order (null / 0) — back to the default order.
          setSortOrder(e.sortOrder === 1 || e.sortOrder === -1 ? e.sortOrder : null);
        }}
        rowGroupMode="subheader"
        groupRowsBy="group"
        rowGroupHeaderTemplate={groupHeader}
        tableStyle={{ tableLayout: 'auto' }}
        tableClassName="table"
        className="table-wrapper table-wrapper--stack"
        responsiveLayout="stack"
      >
        <Column
          field="name"
          header={t('member')}
          sortable
          headerClassName="table__header"
          bodyClassName="table__cell table__cell--stack-title"
          body={memberBody}
        />
        <Column
          header={t('role')}
          headerClassName="table__header"
          bodyClassName="table__cell"
          body={roleBody}
          style={{ width: '160px', minWidth: '160px' }}
        />
        <Column
          field="invited"
          header={t('invitedAt')}
          sortable
          headerClassName="table__header"
          bodyClassName="table__cell"
          body={(row: MemberRow) => formatLongDate(row.invited, i18n.language)}
          style={{ width: '160px', minWidth: '160px' }}
        />
        <Column
          header={t('status')}
          headerClassName="table__header"
          bodyClassName="table__cell"
          body={statusBody}
          style={{ width: '110px', minWidth: '110px' }}
        />
        <Column
          header={t('projects')}
          headerClassName="table__header"
          bodyClassName="table__cell table__cell--stack-wide"
          body={projectsBody}
        />
        <Column
          header={t('action')}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell table__cell--center table__cell--stack-actions"
          body={actionsBody}
          style={{ width: '80px', minWidth: '80px' }}
        />
      </DataTable>

      <GuestScopeDialog
        visible={scopeTarget !== null}
        initialIds={scopeTarget?.projectIds ?? []}
        onSave={saveScope}
        onHide={closeScope}
        saving={scopeSaving || roleSaving}
        error={scopeError}
      />
      <ConfirmDialog className="dialog-box" />
      {toastElement}
    </div>
  );
};

export default TeamSection;
